import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { Home, Wallet, TrendingDown, Target, ShoppingCart, BarChart3, BookOpen, Settings, LogOut, Clock } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useEffect, useState, useCallback } from 'react'
import { api } from '../services/api'
import { useInactivityLogout } from '../hooks/useInactivityLogout'

const navItems = [
  { to: '/', icon: Home, label: 'Accueil' },
  { to: '/budget', icon: Wallet, label: 'Budget' },
  { to: '/expenses', icon: TrendingDown, label: 'Dépenses' },
  { to: '/projects', icon: Target, label: 'Projets' },
  { to: '/groceries', icon: ShoppingCart, label: 'Courses' },
  { to: '/history', icon: BarChart3, label: 'Historique' },
  { to: '/cahier', icon: BookOpen, label: 'Cahier' },
  { to: '/settings', icon: Settings, label: 'Réglages' },
]

export default function Layout({ children }) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [showInactivityWarning, setShowInactivityWarning] = useState(false)
  const initials = (user?.name || '')
    .split(' ')
    .filter(Boolean)
    .map(p => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?'

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const handleAutoLogout = useCallback(async () => {
    if (user?.phone) {
      localStorage.setItem('budget_last_phone', user.phone)
    }
    setShowInactivityWarning(false)
    await logout()
    navigate('/login')
  }, [user, logout, navigate])

  const handleInactivityWarning = useCallback(() => {
    setShowInactivityWarning(true)
  }, [])

  useInactivityLogout({ onLogout: handleAutoLogout, onWarning: handleInactivityWarning })

  // Presence heartbeat
  useEffect(() => {
    if (!user) return
    api.updatePresence(location.pathname).catch(() => {})
  }, [location.pathname, user])

  useEffect(() => {
    if (!user) return
    const id = setInterval(() => {
      api.updatePresence(location.pathname).catch(() => {})
    }, 25000)
    return () => clearInterval(id)
  }, [user, location.pathname])

  // Global events poller (Option B): fetch events every 5s and dispatch a browser event
  useEffect(() => {
    if (!user) return
    let since = Date.now() - 60 * 1000 // start with last minute
    let cancelled = false
    const tick = async () => {
      try {
        const res = await api.getEvents(since)
        if (cancelled) return
        const events = res.events || []
        if (events.length > 0) {
          since = Date.parse(events[events.length - 1].ts) || since
          // Broadcast for any interested page to react/refetch selectively
          window.dispatchEvent(new CustomEvent('fb:events', { detail: events }))
        }
      } catch {}
    }
    const id = setInterval(tick, 5000)
    tick()
    return () => { cancelled = true; clearInterval(id) }
  }, [user])

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">

      {/* Bandeau avertissement inactivité */}
      {showInactivityWarning && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-white px-4 py-3 flex items-center justify-between shadow-lg safe-top">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Clock size={16} className="shrink-0" />
            <span>Déconnexion automatique dans 2 minutes pour inactivité</span>
          </div>
          <button
            onClick={() => { setShowInactivityWarning(false) }}
            className="text-white/80 text-xs underline ml-3 shrink-0"
          >
            Rester connecté
          </button>
        </div>
      )}

      {/* Top bar */}
      <header className="safe-top bg-primary-700 text-white sticky top-0 z-30">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="text-2xl">💰</span>
            <span className="font-bold text-xl">Budget Familial</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-white/20 text-white flex items-center justify-center text-sm font-semibold">
              {initials}
            </div>
            <button title="Déconnexion" onClick={handleLogout} className="flex items-center gap-1.5 px-3 py-2 rounded-lg active:bg-white/15 transition-colors text-sm font-medium">
              <LogOut size={16} />
              <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 px-4 py-4 pb-24 max-w-2xl mx-auto w-full">
        {children}
      </main>

      {/* Bottom navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 safe-bottom z-30">
        <div className="max-w-2xl mx-auto grid grid-cols-8 gap-0.5 px-1 py-1">
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 py-1.5 px-0.5 rounded-lg transition-colors ${
                  isActive ? 'text-primary-700 bg-primary-50' : 'text-slate-400'
                }`
              }
            >
              <Icon size={20} />
              <span className="text-[9px] font-medium leading-none">{label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  )
}
